import { useState } from "react";
import { SlidePanel } from "@/components/ui/slide-panel";
import { ExtendedProject } from "@/types/project";

// ============================================================================
// Types
// ============================================================================

interface ProjectStatusPanelProps {
  isOpen: boolean;
  onClose: () => void;
  project: ExtendedProject | null;
  onStatusChange: (status: string) => Promise<void>;
}

// ============================================================================
// Constants
// ============================================================================

const AVAILABLE_STATUSES = ["Active", "Hold", "Cancelled", "Complete"];

// ============================================================================
// Component
// ============================================================================

export function ProjectStatusPanel({
  isOpen,
  onClose,
  project,
  onStatusChange,
}: ProjectStatusPanelProps) {
  const [statusSearch, setStatusSearch] = useState("");

  const handleClose = () => {
    setStatusSearch("");
    onClose();
  };

  const handleStatusSelect = async (status: string) => {
    await onStatusChange(status);
    handleClose();
  };

  const filteredStatuses = AVAILABLE_STATUSES.filter((status) =>
    status.toLowerCase().includes(statusSearch.toLowerCase())
  );

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Change Project Status"
      width="sm"
      position="right"
      showBackdrop={true}
      closeOnBackdropClick={true}
      topOffset="calc(4rem + 10px)"
      panelClassName="border-l-2 border-t-2 border-border rounded-tl-lg"
      backdropClassName="!left-0"
      immediateBackdrop={true}
      showCloseButton={true}
      zIndex={220}
    >
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search status..."
          className="w-full border rounded px-3 py-2 mb-3"
          value={statusSearch}
          onChange={(e) => setStatusSearch(e.target.value)}
          autoFocus
        />
        <div className="space-y-1">
          {filteredStatuses.map((status) => (
            <div
              key={status}
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors hover:bg-muted ${
                project?.project_status === status
                  ? "bg-primary/10 border border-primary/20"
                  : ""
              }`}
              onClick={() => handleStatusSelect(status)}
            >
              <span className="font-medium">{status}</span>
              {project?.project_status === status && (
                <span className="text-xs text-primary font-semibold">
                  CURRENT
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </SlidePanel>
  );
}

ProjectStatusPanel.displayName = "ProjectStatusPanel";
